import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";
import { apiDelete, apiGet, apiPost, resetUnauthorizedGuard } from "@/shared/api/client";
import { queryKeys } from "@/shared/api/query-keys";
import type { AccountDto, OperatorDto, OperatorMeDto } from "@/shared/api/types";
import { isAdmin } from "./roles";

/** The signed-in operator. Routes under the shell only render with one of these. */
export interface OperatorSession {
  operator: OperatorDto;
  sessionMinutes: number;
  admin: boolean;
}

const OperatorContext = createContext<OperatorSession | null>(null);

export function OperatorProvider({ value, children }: { value: OperatorMeDto; children: ReactNode }) {
  const session: OperatorSession = {
    operator: value.operator,
    sessionMinutes: value.sessionMinutes,
    admin: isAdmin(value.operator.role),
  };
  return <OperatorContext.Provider value={session}>{children}</OperatorContext.Provider>;
}

export function useOperatorSession(): OperatorSession {
  const s = useContext(OperatorContext);
  if (!s) throw new Error("useOperatorSession must be used inside an operator route");
  return s;
}

/** `GET /internal/me`: the operator behind the `nacs_operator` cookie; 401/403 means sign in again. */
export function useOperatorMeQuery(enabled = true) {
  return useQuery({
    queryKey: queryKeys.session.operator(),
    queryFn: ({ signal }) => apiGet<OperatorMeDto>("/internal/me", undefined, signal),
    staleTime: 5 * 60_000,
    retry: false,
    enabled,
  });
}

/** `GET /me`: the person behind the customer cookie, so the login page can re-issue the operator session without a code. */
export function useAccountQuery(enabled = true) {
  return useQuery({
    queryKey: queryKeys.session.account(),
    queryFn: ({ signal }) => apiGet<AccountDto>("/me", undefined, signal),
    staleTime: 60_000,
    retry: false,
    enabled,
  });
}

/**
 * A new session (verify, operator-session) means a new cache: nothing from the previous operator survives
 * (rules/01 §3, belt and braces), and the next expired session may redirect again.
 */
export function beginSession(queryClient: QueryClient): void {
  resetUnauthorizedGuard();
  queryClient.clear();
}

/** `POST /auth/operator-session`: turns a customer session into an operator session; 403 OPERATOR_REQUIRED otherwise. */
export function useStartOperatorSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<OperatorMeDto>("/auth/operator-session"),
    onSuccess: (me) => {
      beginSession(queryClient);
      queryClient.setQueryData(queryKeys.session.operator(), me);
    },
  });
}

/**
 * Sign out ends the operator session and the customer session behind it (the api origin may hold a support
 * session too), then drops every cached row. Each call is best-effort: a cookie that is already gone is not an error.
 */
export function useSignOut(onDone: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiDelete<{ ended: boolean }>("/auth/operator-session").catch(() => undefined);
      await apiPost<Record<string, never>>("/auth/logout").catch(() => undefined);
    },
    onSettled: () => {
      queryClient.clear();
      onDone();
    },
  });
}
